import React, { useEffect, useState } from "react"
import { IconPlayerPlay } from "@tabler/icons-react"
import type { ISermon } from "@dc-ireland/shared"
import apiClient from "../../lib/api"
import LandingButton from "./LandingButton"
import LandingCarousel from "./LandingCarousel"
import LandingEmptyState from "./LandingEmptyState"
import LandingReveal from "./LandingReveal"

const cardWrapperClass = "shrink-0 snap-start basis-[240px] sm:basis-[260px] md:basis-[280px]"

const LandingSermonsSection: React.FC = () => {
  const [sermons, setSermons] = useState<ISermon[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchSermons = async () => {
      try {
        const data = await apiClient.getSermons()
        setSermons(data)
      } catch (err) {
        console.error("Error fetching sermons:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchSermons()
  }, [])

  const hasSermons = sermons.length > 0

  return (
    <section id="sermons" className="relative scroll-mt-24 overflow-hidden bg-[#0a0a12] py-16 md:py-20">
      <div
        className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
        style={{ backgroundImage: "url('/year-of-the-eagle-2026.png')" }}
        aria-hidden
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#001a4d]/90 via-[#0d0d0d]/85 to-[#0d0d0d]/95" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,rgba(158,126,67,0.12),transparent_55%)]" />

      <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-8 px-4 md:px-8 lg:px-20">
        <LandingReveal className="flex max-w-2xl flex-col items-center gap-2 text-center">
          <p className="font-dm-sans text-xs font-semibold uppercase tracking-[0.22em] text-[#9e7e43]">
            The Year of the Eagle
          </p>
          <h2 className="font-outfit text-3xl font-semibold tracking-tight text-white md:text-4xl">
            Inspiring Sermons
          </h2>
          <p className="font-dm-sans text-base leading-7 tracking-wide text-white/70">
            Catch up on recent messages from our Year of the Eagle series.
          </p>
        </LandingReveal>

        {loading ? (
          <div className="flex justify-center py-8">
            <span className="loading loading-spinner loading-lg text-[#9e7e43]" />
          </div>
        ) : hasSermons ? (
          <>
            <LandingReveal delay={0.08} className="w-full">
              <LandingCarousel
                ariaLabel="Sermon videos carousel"
                className="mx-auto w-full max-w-6xl"
              >
                {sermons.map(({ id, slug, title, embedUrl }) => (
                  <div key={id ?? slug} className={cardWrapperClass}>
                    <article className="group flex flex-col gap-2">
                      <div className="relative overflow-hidden rounded-xl shadow-[0_12px_32px_-12px_rgba(0,0,0,0.45)] ring-1 ring-white/10 transition duration-300 group-hover:ring-[#9e7e43]/30">
                        <div className="aspect-video w-full">
                          <iframe
                            className="h-full w-full"
                            src={embedUrl}
                            title={title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                          />
                        </div>
                      </div>
                      <h3 className="truncate font-outfit text-sm font-medium tracking-tight text-white/90">
                        {title}
                      </h3>
                    </article>
                  </div>
                ))}
              </LandingCarousel>
            </LandingReveal>

            <LandingReveal delay={0.12}>
              <LandingButton
                variant="outline"
                href="https://www.youtube.com/@dominioncitydublin"
              >
                Watch More on YouTube
              </LandingButton>
            </LandingReveal>
          </>
        ) : (
          <LandingEmptyState
            variant="dark"
            pattern="play"
            icon={IconPlayerPlay}
            title="Fresh messages coming soon"
            description="Our latest sermons will appear here as they're released. In the meantime, explore the full archive on our YouTube channel."
            action={
              <LandingButton variant="outline" href="https://www.youtube.com/@dominioncitydublin">
                Watch on YouTube
              </LandingButton>
            }
          />
        )}
      </div>
    </section>
  )
}

export default LandingSermonsSection
